import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/store/authStore';
import {
  cpSendOTP,
  cpVerifyOTP,
  cpCompleteReverify,
} from '@/lib/api/controlPlaneClient';
import type { LineCallbackParams } from '@/lib/api/controlPlaneClient';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Phone, Shield } from 'lucide-react';

export function PhoneReVerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const loginViaLine = useAuthStore((state) => state.loginViaLine);

  const reverifyToken = searchParams.get('reverify_token');

  // Local state only (NOT registrationStore — G8)
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [otpStep, setOtpStep] = useState<'phone' | 'pin'>('phone');
  const [refCode, setRefCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Countdown timer — ALL hooks before conditional returns (React #300)
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // Guard: redirect if no reverify_token (AFTER all hooks)
  if (!reverifyToken) {
    return <Navigate to="/login" replace />;
  }

  const handleSendOTP = async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await cpSendOTP(phone);
      setRefCode(response.ref_code);
      setOtpStep('pin');
      setCountdown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndLogin = async () => {
    setError(null);
    setLoading(true);
    try {
      await cpVerifyOTP(phone, pin);
      const result = await cpCompleteReverify(reverifyToken, phone);

      const callbackParams: LineCallbackParams = {
        access_token: result.access_token,
        refresh_token: result.refresh_token,
        tenant_id: result.tenant_id,
        user_id: result.user_id,
        server_url: result.server_url,
        company: result.company,
        is_provisioned: result.is_provisioned ? '1' : '0',
        storefront_url: result.storefront_url || '',
        redirect: '/dashboard',
        new_user: '0',
        state: '',
      };
      loginViaLine(callbackParams);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <div className="flex justify-center">
            <Shield className="h-12 w-12 text-primary" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Phone Verification Required
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Your account requires phone verification. Please verify your phone number to continue.
          </p>
        </div>

        {error && (
          <div className="rounded-xs bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        {otpStep === 'phone' ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="08X-XXX-XXXX"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="pl-10"
                  disabled={loading}
                />
              </div>
            </div>
            <Button
              onClick={handleSendOTP}
              disabled={loading || phone.length < 10}
              className="w-full"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>
              ) : (
                'Send OTP'
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-center text-sm text-gray-500 dark:text-gray-400">
              OTP sent to <span className="font-medium text-gray-900 dark:text-gray-100">{phone}</span>
              {refCode && <span className="block">Ref: {refCode}</span>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="pin">OTP Code</Label>
              <Input
                id="pin"
                type="text"
                inputMode="numeric"
                placeholder="Enter OTP code"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                disabled={loading}
                autoFocus
              />
            </div>
            <Button
              onClick={handleVerifyAndLogin}
              disabled={loading || pin.length < 4}
              className="w-full"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</>
              ) : (
                'Verify & Login'
              )}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => { setOtpStep('phone'); setPin(''); setError(null); }}
                className="text-primary hover:underline"
              >
                Change number
              </button>
              {countdown > 0 ? (
                <span className="text-gray-400">Resend in {countdown}s</span>
              ) : (
                <button
                  type="button"
                  onClick={handleSendOTP}
                  disabled={loading}
                  className="text-primary hover:underline"
                >
                  Resend OTP
                </button>
              )}
            </div>
          </div>
        )}

        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}
